/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-06 11:55:26                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-06 16:07:20                               *
 *****************************************************************************/

/* SUMMARY
  * Components
  * Services
  * Validator
  * CompInfosForm: Generic compinfo form
    * handleChange: handle fields change
*/

/* Components */
import InputCompinfoForm from "./InputCompinfoForm";
import SelectCompinfoForm from "./SelectCompinfoForm";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
import { allowedCompInfoUsages, isCompInfoUsable } from "./compinfo-usage";
/***/

/* Validator */
/**
* compInfoValidatorFor: build the compinfo validator for a workorder status
*
* On ne valide que ce qu'on affiche : le back renvoie l'intervention complétée de TOUS les
* compInfos du service, usage compris, si bien qu'une contrainte REQUIRED portée par un champ
* jamais rendu refusait une sauvegarde que personne ne pouvait débloquer.
*
* @param status - statut de l'intervention, absent à la création
* @return - validator descriptor for EasyTech.CompInfos
*/
export const compInfoValidatorFor = (status?: string) => {
  const usages = allowedCompInfoUsages(status);

  return {
    inputs: (e: EasyTech.InputCompinfo[]) => {
      for (let i = 0; i < e.length; i++) {
        if (!isCompInfoUsable(e[i].compInfo, usages)) continue;
        if (e[i].compInfo.constraints === "REQUIRED" && !validator.isValidCompInfo(e[i].compInfo, e[i].value)) return false;
      }
      return true;
    },
    selects: (e: EasyTech.SelectCompInfoValue[]) => {
      for (let i = 0; i < e.length; i++) {
        if (!isCompInfoUsable(e[i].compInfo, usages)) continue;
        if (e[i].compInfo.constraints === "REQUIRED" && !validator.isValidSelectCompInfo(e[i].compInfo, e[i].values.map((el) => el.value))) return false;
      }
      return true;
    }
  };
};
/***/

/**
* CompInfosForm: Generic compinfo form
* @param ressource - compinfo linked ressource
* @param data - previous data
* @param submitted - subimitted form state
* @param onChange - on field change event
* @param onSumit - on form submit event
* @param status - statut de l'intervention, absent à la création
*/
export default function CompInfosForm({
  data,
  submitted,
  onChange,
  onSubmit,
  status
}: {
  ressource: string,
  data?: EasyTech.CompInfos,
  submitted?: boolean,
  onChange: (e: EasyTech.CompInfos) => void, // todo
  onSubmit: () => void, // todo
  status?: string
}) {
  /**
  * handleChange: handle fields change
  * @param type - compinfo type change
  * @param e - new value 
  */
  const handleChange = (type: string, e: EasyTech.InputCompinfo[] | EasyTech.SelectCompInfoValue[]) => {
    const input = data && data.inputs ? data.inputs : [];
    const select = data && data.selects ? data.selects : [];
    const ret = {inputs: input, selects: select} as EasyTech.CompInfos;
    ret[type] = e;
    
    // AJOUT: Déduplication pour éviter les compinfos en double
    if (ret.inputs) {
      ret.inputs = ret.inputs.filter((item, index, self) =>
        index === self.findIndex(x => x.compInfo.id === item.compInfo.id)
      );
    }
    
    if (ret.selects) {
      ret.selects = ret.selects.filter((item, index, self) =>
        index === self.findIndex(x => x.compInfo.id === item.compInfo.id)
      );
    }
    
    onChange(ret);
  };
  /***/
  
  return (
    <div className="flex flex-col">
      <InputCompinfoForm  data={(data && data.inputs) ? data.inputs : []}
                          onChange={(e) => handleChange("inputs", e)}
                          onSubmit={onSubmit}
                          submitted={submitted}
                          ressource="WORKORDER"
                          status={status}/>

      <SelectCompinfoForm data={(data && data.selects) ? data.selects : []}
                          onChange={(e) => handleChange("selects", e)}
                          onSubmit={onSubmit}
                          submitted={submitted}
                          ressource="WORKORDER"
                          status={status}/>
    </div>
  );
}
/***/
